export interface RagLink {
  source_id: string;
  kind: string;
  title?: string;
  cms: {
    url?: string;
    image_url?: string;
    title?: string;
    item_type?: string;
  };
}

export interface ConversationEntry {
  id: number | string;
  text: string;
  date: string;
  conversationId?: string;
}

export interface MessageQuota {
  allowed?: boolean;
  remaining?: number;
  remaining_messages?: number;
  total?: number;
  message_limit?: number;
  resets_at?: string;
}

export interface IncompleteChapter {
  id?: string;
  url: string;
  title: string;
}

export interface TrainingProgress {
  foundation_complete?: boolean;
  available?: boolean;
  first_incomplete_chapters?: IncompleteChapter[];
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  streaming: boolean;
  links: RagLink[];
}

export type HistoryMessage = Record<string, unknown>;

export interface HistoryData {
  messages: HistoryMessage[];
  links: RagLink[];
  conversationId?: string;
  conversation_id?: string;
}

export interface StreamEvent {
  message_quota?: MessageQuota;
  data?: {
    message_quota?: MessageQuota;
    training_progress?: TrainingProgress;
    conversation_id?: string;
    conversationId?: string;
    links?: RagLink[];
    text?: string;
    content?: string;
    delta?: string;
    message?: string;
  };
  training_progress?: TrainingProgress;
  links?: RagLink[];
  text?: string;
  content?: string;
  delta?: string;
  message?: string;
  conversationId?: string;
  conversation_id?: string;
  id?: string;
  kind?: string;
  type?: string;
  event?: string;
}

export class SmartAssistApiError extends Error {
  status?: number;
  messageQuota?: MessageQuota;
  trainingProgress?: TrainingProgress;
  conversationId?: string;

  constructor(
    message: string,
    options?: {
      status?: number;
      messageQuota?: MessageQuota;
      trainingProgress?: TrainingProgress;
      conversationId?: string;
    },
  ) {
    super(message);
    this.name = 'SmartAssistApiError';
    this.status = options?.status;
    this.messageQuota = options?.messageQuota;
    this.trainingProgress = options?.trainingProgress;
    this.conversationId = options?.conversationId;
  }
}

interface BaseParams {
  baseUrl: string;
  token: string;
  appId: string;
}

interface ChatHistoryParams extends BaseParams {
  conversationId: string;
  signal?: AbortSignal;
}

export interface StreamChatParams extends BaseParams {
  message: string;
  conversationId: string | null;
  signal: AbortSignal;
  onEvent(this: void, event: StreamEvent): void;
}

interface DeleteConversationParams {
  baseUrl: string;
  token: string;
  conversationId: string;
}

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const authHeaders = (token: string): Record<string, string> => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
  Accept: 'application/json, text/event-stream',
});

const responseBody = async (response: Response): Promise<Record<string, unknown> | null> => {
  try {
    return toRecord(await response.json());
  } catch {
    return null;
  }
};

const responseMessage = (response: Response, body: Record<string, unknown> | null): string =>
  typeof body?.message === 'string'
    ? body.message
    : typeof body?.error === 'string'
      ? body.error
      : response.statusText;

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const isStreamEvent = (value: unknown): value is StreamEvent =>
  value !== null && typeof value === 'object';

const parseStreamPayload = (raw: string, eventName?: string): StreamEvent | null => {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '[DONE]') {
    return trimmed === '[DONE]' ? { kind: 'done' } : null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (isStreamEvent(parsed)) {
      return eventName && !parsed.kind && !parsed.type && !parsed.event
        ? { ...parsed, event: eventName }
        : parsed;
    }
  } catch {
    return { kind: eventName ?? 'delta', delta: raw };
  }

  return null;
};

const emitSseBlock = (block: string, onEvent: StreamChatParams['onEvent']): void => {
  const eventName = block
    .split(/\r?\n/)
    .find((line) => line.startsWith('event:'))
    ?.slice(6)
    .trim();
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');
  const event = parseStreamPayload(data, eventName);
  if (event) onEvent(event);
};

const emitJsonLine = (line: string, onEvent: StreamChatParams['onEvent']): void => {
  const event = parseStreamPayload(line);
  if (event) onEvent(event);
};

const suggestionText = (value: unknown): string | null => {
  if (typeof value === 'string') return value.trim() || null;
  const record = toRecord(value);
  if (!record) return null;

  const text = record.text ?? record.title ?? record.label ?? record.question ?? record.prompt;
  return typeof text === 'string' && text.trim() ? text.trim() : null;
};

const normalizeSuggestions = (body: unknown): string[] => {
  const direct = Array.isArray(body) ? body : null;
  const record = toRecord(body);
  const nested = record?.suggestions ?? record?.items ?? toRecord(record?.data)?.suggestions ?? toRecord(record?.data)?.items;
  const list = direct ?? (Array.isArray(nested) ? nested : []);

  return list.map(suggestionText).filter((item): item is string => Boolean(item));
};

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const firstNonEmptyArray = (...values: unknown[]): unknown[] => values.map(asArray).find((items) => items.length > 0) ?? [];

const nestedData = (body: unknown): Record<string, unknown> | null => toRecord(toRecord(body)?.data);

const conversationText = (value: unknown): string | null => {
  const record = toRecord(value);
  if (!record) return null;
  return asString(
    record.text ??
      record.title ??
      record.name ??
      record.summary ??
      record.last_message ??
      record.lastMessage ??
      record.first_message ??
      record.firstMessage ??
      record.question ??
      record.message ??
      record.content,
  );
};

const conversationDateValue = (value: unknown): string | number | null => {
  const record = toRecord(value);
  if (!record) return null;
  const raw =
    record.date ??
    record.created_at ??
    record.createdAt ??
    record.updated_at ??
    record.updatedAt ??
    record.timestamp ??
    record.last_message_at ??
    record.lastMessageAt;
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  return null;
};

const conversationIdFromRecord = (value: unknown): string | null => {
  const record = toRecord(value);
  if (!record) return null;
  return asString(record.conversationId ?? record.conversation_id ?? record.thread_id ?? record.threadId ?? record.id);
};

const normalizeConversations = (body: unknown): ConversationEntry[] => {
  const record = toRecord(body);
  const data = nestedData(body);
  const list = firstNonEmptyArray(body, record?.conversations, record?.items, data?.conversations, data?.items, data?.threads);

  return list
    .map((item, index): ConversationEntry | null => {
      const conversationId = conversationIdFromRecord(item);
      const text = conversationText(item);
      if (!conversationId || !text) return null;
      const dateValue = conversationDateValue(item);
      return {
        id: conversationId || index,
        conversationId,
        text,
        date: dateValue == null ? '' : formatHistoryDate(dateValue),
      };
    })
    .filter((item): item is ConversationEntry => Boolean(item));
};

const normalizeHistory = (body: unknown): HistoryData => {
  const record = toRecord(body);
  const data = nestedData(body);
  const messages = firstNonEmptyArray(record?.messages, record?.history, data?.messages, data?.history, data?.items);
  const links = firstNonEmptyArray(record?.links, record?.rag_links, data?.links, data?.rag_links) as RagLink[];

  return {
    messages: messages as HistoryMessage[],
    links,
    conversationId: pickConversationId(body) ?? undefined,
    conversation_id: asString(data?.conversation_id) ?? undefined,
  };
};

const historyMessageText = (message: HistoryMessage): string => {
  const record = toRecord(message);
  if (!record) return '';
  return extractMessageText(record.text ?? record.content ?? record.message ?? record.answer ?? record.question ?? record.delta ?? record.value);
};

const extractMessageText = (value: unknown): string => {
  const direct = asString(value);
  if (direct) return direct;

  if (Array.isArray(value)) {
    return value.map(extractMessageText).filter(Boolean).join('\n\n');
  }

  const record = toRecord(value);
  if (!record) return '';

  return extractMessageText(record.text ?? record.content ?? record.message ?? record.value);
};

const historyMessageRole = (message: HistoryMessage): ChatMessage['role'] => {
  const record = toRecord(message);
  const rawRole = asString(record?.role ?? record?.sender ?? record?.type ?? record?.author)?.toLowerCase();
  if (rawRole && ['assistant', 'ai', 'bot', 'agent', 'model', 'system'].includes(rawRole)) return 'assistant';
  return 'user';
};

export async function fetchChatSuggestions({ baseUrl, token, appId }: BaseParams): Promise<string[]> {
  const url = new URL(`${trimTrailingSlash(baseUrl)}/chat/suggestions`);
  url.searchParams.set('app_id', appId);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: authHeaders(token),
  });

  const body = await responseBody(response);
  if (!response.ok) {
    throw new SmartAssistApiError(responseMessage(response, body), { status: response.status });
  }

  return normalizeSuggestions(body);
}

export async function fetchChatConversations({ baseUrl, token, appId }: BaseParams): Promise<ConversationEntry[]> {
  const root = trimTrailingSlash(baseUrl);
  const candidates = [
    `${root}/chat/conversations`,
    `${root}/apps/${appId}/chat/conversations`,
    `${root}/agent/chat/conversations`,
    `${root}/neuron/chat/conversations`,
  ];

  let response: Response | null = null;
  for (const candidate of candidates) {
    const url = new URL(candidate);
    url.searchParams.set('app_id', appId);
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: authHeaders(token),
    });
    if (response.ok || (response.status !== 404 && response.status !== 405)) break;
  }

  if (!response) throw new SmartAssistApiError('Request failed.');

  const body = await responseBody(response);
  if (!response.ok) {
    throw new SmartAssistApiError(responseMessage(response, body), { status: response.status });
  }

  return normalizeConversations(body);
}

export async function fetchChatHistory({
  baseUrl,
  token,
  appId,
  conversationId,
  signal,
}: ChatHistoryParams): Promise<HistoryData> {
  const root = trimTrailingSlash(baseUrl);
  const encodedConversationId = encodeURIComponent(conversationId);
  const candidates = [
    `${root}/chat/conversations/${encodedConversationId}`,
    `${root}/chat/history`,
    `${root}/apps/${appId}/chat/conversations/${encodedConversationId}`,
    `${root}/agent/chat/history`,
    `${root}/neuron/chat/history`,
  ];

  let response: Response | null = null;
  for (const candidate of candidates) {
    const url = new URL(candidate);
    url.searchParams.set('app_id', appId);
    url.searchParams.set('conversation_id', conversationId);
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: authHeaders(token),
      signal,
    });
    if (response.ok || (response.status !== 404 && response.status !== 405)) break;
  }

  if (!response) throw new SmartAssistApiError('Request failed.');

  const body = await responseBody(response);
  if (!response.ok) {
    throw new SmartAssistApiError(responseMessage(response, body), { status: response.status });
  }

  return normalizeHistory(body);
}

export function mapNeuronHistoryToUiMessages(messages: HistoryMessage[] = [], links: RagLink[] = []): ChatMessage[] {
  const firstAssistantLinkIndex = messages.findIndex((message) => historyMessageRole(message) === 'assistant');

  return messages
    .map((message, index): ChatMessage => {
      const role = historyMessageRole(message);
      const record = toRecord(message);
      const messageLinks = firstNonEmptyArray(record?.links, record?.rag_links, record?.citations) as RagLink[];
      return {
        id: index + 1,
        role,
        text: historyMessageText(message),
        streaming: false,
        links: messageLinks.length > 0 || role !== 'assistant' || index !== firstAssistantLinkIndex ? messageLinks : links,
      };
    })
    .filter((message) => message.text || message.links.length > 0);
}

export function pickConversationId(data: unknown): string | null {
  const record = toRecord(data);
  if (!record) return null;

  const direct = record.conversationId ?? record.conversation_id ?? record.thread_id ?? record.threadId ?? record.id;
  const directId = asString(direct);
  if (directId) return directId;

  const nested = toRecord(record.data);
  return asString(nested?.conversationId ?? nested?.conversation_id ?? nested?.thread_id ?? nested?.threadId ?? nested?.id);
}

export async function streamAgentChat({
  baseUrl,
  token,
  appId,
  message,
  conversationId,
  signal,
  onEvent,
}: StreamChatParams): Promise<void> {
  const root = trimTrailingSlash(baseUrl);
  const payload = {
    app_id: appId,
    appId,
    message,
    conversation_id: conversationId,
    conversationId,
  };
  const candidates = [
    `${root}/chat/stream`,
    `${root}/apps/${appId}/chat/stream`,
    `${root}/agent/chat/stream`,
    `${root}/neuron/chat/stream`,
  ];

  let response: Response | null = null;
  for (const url of candidates) {
    response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(payload),
      signal,
    });
    if (response.ok || (response.status !== 404 && response.status !== 405)) break;
  }

  if (!response) throw new SmartAssistApiError('Request failed.');

  if (!response.ok) {
    const bodyRecord = await responseBody(response);
    const messageText = responseMessage(response, bodyRecord);
    throw new SmartAssistApiError(messageText, {
      status: response.status,
      messageQuota: (bodyRecord?.message_quota ?? toRecord(bodyRecord?.data)?.message_quota) as MessageQuota | undefined,
      trainingProgress: (bodyRecord?.training_progress ?? toRecord(bodyRecord?.data)?.training_progress) as TrainingProgress | undefined,
      conversationId: pickConversationId(bodyRecord) ?? undefined,
    });
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const json = (await response.json()) as unknown;
    const event = parseStreamPayload(JSON.stringify(json));
    if (event) onEvent(event);
    onEvent({ kind: 'done' });
    return;
  }

  if (!response.body) {
    const json = (await response.json()) as unknown;
    const event = parseStreamPayload(JSON.stringify(json));
    if (event) onEvent(event);
    onEvent({ kind: 'done' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const isSse = contentType.includes('text/event-stream');

  let reading = true;
  while (reading) {
    const { value, done } = await reader.read();
    if (done) {
      reading = false;
      break;
    }
    if (value === undefined) continue;

    buffer += decoder.decode(value, { stream: true });

    if (isSse || buffer.includes('data:')) {
      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() ?? '';
      parts.forEach((part) => {
        emitSseBlock(part, onEvent);
      });
    } else {
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      lines.filter(Boolean).forEach((line) => {
        emitJsonLine(line, onEvent);
      });
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    if (isSse || buffer.includes('data:')) emitSseBlock(buffer, onEvent);
    else emitJsonLine(buffer, onEvent);
  }

  onEvent({ kind: 'done' });
}

export function streamEventKind(evt: StreamEvent): string {
  const explicit = evt.kind ?? evt.type ?? evt.event;
  if (explicit) return explicit;
  if ((evt.links?.length ?? evt.data?.links?.length ?? 0) > 0) return 'links';
  if (typeof evt.delta === 'string' || typeof evt.text === 'string' || typeof evt.content === 'string') return 'delta';
  if (evt.message) return 'message';
  return '';
}

export async function deleteChatConversation({
  baseUrl,
  token,
  conversationId,
}: DeleteConversationParams): Promise<void> {
  const root = trimTrailingSlash(baseUrl);
  const encodedConversationId = encodeURIComponent(conversationId);
  const candidates = [
    `${root}/chat/conversations/${encodedConversationId}`,
    `${root}/chat/history/${encodedConversationId}`,
    `${root}/agent/chat/conversations/${encodedConversationId}`,
    `${root}/neuron/chat/conversations/${encodedConversationId}`,
  ];

  let response: Response | null = null;
  for (const url of candidates) {
    response = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(token),
    });
    if (response.ok || (response.status !== 404 && response.status !== 405)) break;
  }

  if (!response) throw new SmartAssistApiError('Request failed.');
  if (!response.ok) {
    const body = await responseBody(response);
    throw new SmartAssistApiError(responseMessage(response, body), { status: response.status });
  }
}

export function formatHistoryDate(timestamp: number | string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
